import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { Reveal, MaskedLine } from "@/components/Reveal";
import { IMAGES } from "@/config";

const PRODUCTS = [
  {
    num: "01",
    layer: "Interact",
    name: "AI Voice Agent",
    italic: "Voice",
    tag: "Prototype demonstrated",
    desc: "An automated voice agent that handles inbound and outbound customer calls, configured against your knowledge base — extending service availability without linear staffing growth.",
    does: ["Inbound & outbound calling", "Knowledge-base driven answers", "Configurable per business or product", "20+ languages supported", "Parallel call handling", "Available 24×7"],
    uses: ["Membership & benefit questions", "Billing and payment enquiries", "First-level support and FAQ", "Initial call triage", "Outbound notifications & reminders"],
    value: "Fewer routine agent-handled interactions, service that scales at peak volume, and support that never sleeps.",
    img: IMAGES.wave,
  },
  {
    num: "02",
    layer: "Interact",
    name: "Live Transcription & Translation",
    italic: "Transcription",
    tag: "Prototype demonstrated",
    desc: "Converts a live customer conversation into structured text — and helps your agents understand a customer speaking another language, in real time.",
    does: ["Real-time transcription of ongoing calls", "Multilingual conversation support", "Agent-readable translation", "Structured text for downstream AI"],
    uses: ["Multilingual customer support", "Call documentation without note-taking", "Foundation for sentiment intelligence", "Compliance-ready transcripts"],
    value: "Stronger multilingual support, less communication friction and rework, and a structured record of every conversation.",
    img: IMAGES.lines,
  },
  {
    num: "03",
    layer: "Understand",
    name: "Sentiment & Conversation Intelligence",
    italic: "Intelligence",
    tag: "Prototype demonstrated",
    desc: "Turns a transcript into a decision the agent can act on immediately — what the customer is feeling, what they're asking for, and what to do next.",
    does: ["Sentiment classification — positive, neutral, negative", "Customer tone & key insights", "Conversation summaries", "Action items and recommended next steps"],
    uses: ["Real-time agent guidance", "Early identification of frustrated customers", "Escalation handling & consistency", "Conversation intelligence at scale"],
    value: "Faster agent understanding, better handling of difficult conversations, and intelligence applied to every call — not a sample.",
    img: IMAGES.approach,
  },
  {
    num: "04",
    layer: "Understand",
    name: "AI Digital Twin",
    italic: "Twin",
    tag: "Concept",
    desc: "One unified profile that reasons about the customer, not just records them — who they are, what they value, and what they're likely to do next.",
    does: ["Dynamic digital profile of every customer", "Unifies data from every platform & touchpoint", "Reasons from the customer's perspective", "Evolves continuously with new interactions"],
    uses: ["Single 360° customer view", "Behaviour, preference & affinity mapping", "Lifecycle stage detection", "Benefits usage & engagement analysis"],
    value: "Every downstream decision — retention, offers, outreach — grounded in a profile that understands why customers behave the way they do.",
    img: IMAGES.hero,
  },
  {
    num: "05",
    layer: "Protect Value",
    name: "ChurnSense AI",
    italic: "ChurnSense",
    tag: "Predictive churn intelligence",
    desc: "Predicts who will churn, why, when, and how much revenue is at risk — so retention effort goes where impact is greatest.",
    does: ["WHO — customers scored by churn risk", "WHY — explainable behavioural churn drivers", "WHEN — predicted churn window, e.g. 14–30 days", "HOW MUCH — revenue at risk per customer", "WHAT NEXT — prioritized retention action"],
    uses: ["Risk segmentation from low to critical", "Proactive retention triggers", "Revenue-at-risk forecasting", "Prioritized intervention queues"],
    value: "Earlier identification of churn risk, better prioritization of customers, and more targeted retention action.",
    img: IMAGES.curves,
  },
  {
    num: "06",
    layer: "Protect Value",
    name: "RetainAI",
    italic: "RetainAI",
    tag: "Personalized retention decisioning",
    desc: "ChurnSense identifies the risk — RetainAI decides the intervention: the minimum effective offer that retains the customer profitably.",
    does: ["WHO — risk & value decide who gets an offer", "WHAT — offer matched to category & merchant affinity", "WHEN — timing tuned to behaviour", "HOW MUCH — minimum effective incentive, not maximum discount", "WHICH CHANNEL — the one most likely to drive redemption"],
    uses: ["Next-best-offer recommendation", "Incentive spend optimization", "Merchant-funded discount matching", "Redemption & lift prediction"],
    value: "Targeted retention instead of blanket incentives — spend only where it changes behaviour, and protect customer lifetime value.",
    img: IMAGES.glass,
  },
  {
    num: "07",
    layer: "Protect Value",
    name: "WinBack AI",
    italic: "WinBack",
    tag: "Reactivation & offer optimization",
    desc: "Identifies previously churned customers with a high likelihood of returning — and determines the most effective, personalized strategy to win them back.",
    does: ["Return-probability scoring", "AI-generated actionable segments", "Per-segment strategy — from reminder-only to premium reactivation", "Repeat-churner root-cause analysis"],
    uses: ["High-probability reactivation campaigns", "High-CLV recovery prioritization", "Low-cost engagement tests", "Incentive discipline for repeat churners"],
    value: "Recover previously lost customers with personalized campaigns — and stop spending on customers who won't return.",
    img: IMAGES.tunnel,
  },
];

export default function Capabilities() {
  return (
    <main data-testid="capabilities-page">
      <header className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src={IMAGES.geometric} alt="" className="w-full h-full object-cover opacity-25" />
          <div className="absolute inset-0 bg-gradient-to-r from-void via-void/75 to-void/30" />
          <div className="absolute inset-0 bg-gradient-to-t from-void via-transparent to-void/60" />
        </div>
        <div className="relative max-w-[1440px] mx-auto px-6 md:px-12 pt-40 pb-24 md:pb-32">
        <Reveal y={20}>
          <p className="font-mono-tech text-xs uppercase tracking-[0.25em] text-vermilion">Capabilities</p>
        </Reveal>
        <h1 className="mt-8 font-display text-5xl md:text-7xl tracking-tighter leading-[0.95] text-bone max-w-5xl">
          <MaskedLine delay={0.15}>Seven capabilities.</MaskedLine>
          <MaskedLine delay={0.3}>
            Three layers. <span className="italic text-vermilion">One lifecycle.</span>
          </MaskedLine>
        </h1>
        <Reveal delay={0.45}>
          <p className="mt-10 max-w-xl text-ash text-base md:text-lg leading-relaxed">
            Each product works alone. Together they form a single operating layer that interacts, understands,
            and protects value across the entire customer journey.
          </p>
        </Reveal>
        </div>
      </header>

      {PRODUCTS.map((p, i) => (
        <section
          key={p.num}
          data-testid={`product-section-${p.num}`}
          className="border-t border-line py-24 md:py-36 relative overflow-hidden"
        >
          <div className="max-w-[1440px] mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
            <div className={`lg:col-span-7 ${i % 2 === 1 ? "lg:order-2" : ""}`}>
              <Reveal>
                <div className="flex items-center gap-4">
                  <span className="font-mono-tech text-sm text-vermilion">{p.num}</span>
                  <span className="h-px w-12 bg-vermilion" />
                  <span className="font-mono-tech text-[10px] uppercase tracking-[0.25em] text-ash">
                    {p.layer} · {p.tag}
                  </span>
                </div>
                <h2 className="mt-8 font-display text-4xl md:text-6xl tracking-tight text-bone leading-[1.02]">
                  {p.name.split(p.italic)[0]}
                  <span className="italic text-vermilion">{p.italic}</span>
                  {p.name.split(p.italic)[1]}
                </h2>
                <p className="mt-8 max-w-xl text-ash text-base md:text-lg leading-relaxed">{p.desc}</p>
              </Reveal>

              <Reveal delay={0.15}>
                <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 gap-10">
                  <div>
                    <p className="font-mono-tech text-[10px] uppercase tracking-[0.25em] text-vermilion mb-5">What it does</p>
                    <ul className="space-y-3">
                      {p.does.map((d) => (
                        <li key={d} className="text-bone/85 text-sm leading-relaxed border-l border-line pl-4">{d}</li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <p className="font-mono-tech text-[10px] uppercase tracking-[0.25em] text-vermilion mb-5">Candidate use cases</p>
                    <ul className="space-y-3">
                      {p.uses.map((u) => (
                        <li key={u} className="text-ash text-sm leading-relaxed border-l border-line pl-4">{u}</li>
                      ))}
                    </ul>
                  </div>
                </div>
                <div className="mt-12 border border-line p-8 bg-surface/40">
                  <p className="font-mono-tech text-[10px] uppercase tracking-[0.25em] text-vermilion mb-3">Potential value</p>
                  <p className="text-bone/90 text-sm md:text-base leading-relaxed">{p.value}</p>
                </div>
              </Reveal>
            </div>

            <div className={`lg:col-span-5 ${i % 2 === 1 ? "lg:order-1" : ""}`}>
              <Reveal delay={0.2}>
                <div className="relative border border-line overflow-hidden group max-h-[80vh]">
                  <img
                    src={p.img}
                    alt={`${p.name} abstract texture`}
                    className="w-full h-[420px] lg:h-[560px] object-cover opacity-60 transition-all duration-700 group-hover:opacity-80 group-hover:scale-[1.03]"
                    style={{ objectPosition: `${(i * 17) % 80 + 10}% ${(i * 29) % 80 + 10}%` }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-void via-transparent to-transparent" />
                  <div className="absolute bottom-6 left-6 right-6 flex justify-between items-end">
                    <span className="font-mono-tech text-6xl md:text-7xl text-bone/20 group-hover:text-vermilion/60 transition-colors duration-700">{p.num}</span>
                    <span className="font-mono-tech text-[10px] uppercase tracking-[0.25em] text-bone/60">{p.layer}</span>
                  </div>
                </div>
              </Reveal>
            </div>
          </div>
        </section>
      ))}

      <section className="border-t border-line py-32 md:py-40">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12">
          <Reveal>
            <h2 className="font-display text-4xl md:text-6xl tracking-tight text-bone max-w-3xl leading-[1.02]">
              See them working <span className="italic text-vermilion">as one.</span>
            </h2>
            <Link
              to="/contact"
              data-testid="capabilities-cta-demo"
              className="mt-10 inline-flex items-center gap-3 bg-vermilion text-white font-mono-tech text-xs uppercase tracking-[0.2em] px-8 py-5 transition-colors duration-300 hover:bg-vermilion-dark active:scale-[0.98] group"
            >
              Request a demo
              <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
            </Link>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
